import { useMemo, useRef, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";

import { Badge } from "@/components/StatusBadge";
import { CompletenessMeter } from "@/components/CompletenessMeter";
import { KeyFactsStrip, type KeyFact } from "@/components/record/KeyFactsStrip";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { RecordPage } from "@/components/record/RecordPage";
import type { RecordField } from "@/components/record/fields";
import type { RelatedRecordRow } from "@/components/record/RelatedRecords";
import type { DataTableColumn } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Input } from "@/components/ui/Input";
import { PropertiesIcon } from "@/components/ui/icons";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  DocumentListItemResource,
  DocumentReplacementResource,
  EquipmentResource,
  Page,
  PropertyResource,
  ReferenceListItemResource,
  ResolvedSettingResource,
} from "@/lib/api-types";
import { useIsOwner, useIsReadOnly } from "@/lib/current-actor";
import { EQUIPMENT_STATUS_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";

import { AddDocumentDialog } from "./AddDocumentDialog";
import { ReplaceDocumentDialog } from "../documents/ReplaceDocumentDialog";
import { AddEquipmentDialog } from "../equipment/AddEquipmentDialog";

/**
 * `/properties/:id` (Story 5.3). Built on `RecordPage`/`FieldRow` — never a
 * parallel page implementation, the same rule Story 5.2's municipality
 * record page already follows. Exactly five fields render immediately
 * (nickname, full address, municipality, property type, year built — the
 * AC's own "five fields that matter"); the remaining ~20 §4.1 fields sit
 * behind "More details" (I/O matrix).
 *
 * The twelve inheritable municipal settings map `ResolvedSettingResource`
 * onto `RecordField.provenance` directly (spec judgement call 8): the
 * resolver's three real tiers line up with three of `RecordFieldProvenance`'s
 * four; `"confirmed"` is reserved for a field with no legal-default
 * provenance concept at all — nothing here ever uses it, since every field
 * this route passes a `provenance` prop for is one of the twelve.
 *
 * These twelve resolved fields render read-only in this story: the AC asks
 * for their tier/provenance to *render*, never for an override-editing
 * affordance, and the resolved value shown here is a computed read (the
 * override, municipality, or app-default value, whichever tier won) — not
 * the same thing as `<key>_override`, the one column a PATCH would actually
 * need to target. Building that distinct "set an override" control is left
 * for a later pass rather than invented here without a named requirement
 * (Boundaries & Constraints: overrides are read/written only through the
 * property's own record, never a second path — satisfied by not adding a
 * second path at all).
 *
 * Story 5.6 adds the "Archived" badge + explanatory sentence, the Archive
 * (confirmation-gated) / Un-archive (direct, no confirmation — spec
 * judgement call 5, mirroring `UserRecord.tsx`'s own identical "Remove"/
 * "Restore" shape for the same "destructive needs confirming, recovery
 * doesn't" reason) header actions, and the disposition record's own five
 * fields — rendered and, for an Owner, always editable regardless of
 * archive state (no `deletion` prop is passed to `RecordPage`: unlike a
 * removed user, the disposition record stays editable per judgement call
 * 6's "archiving is not blocked on any disposition field, and the
 * disposition record is offered/encouraged on the record page once a
 * property is archived").
 *
 * Tier A fix round, 2026-08-20, Fix 2: every OTHER §4.1 field is editable
 * only while the property is live — `PATCH /properties/{id}` itself now
 * refuses (409) any non-disposition field once a property is archived
 * (`app/api/properties.py::update_property`), and this page's own `fields`
 * list withholds those fields' `edit` config the same way whenever
 * `isArchived` is true, so the UI never offers an edit affordance the API
 * would refuse.
 *
 * Tier A fix round, 2026-08-20, Fix 1: `disposition_closing_documents` is
 * no longer a single comma-separated text field — see
 * `DispositionClosingDocumentsField` below for the real multi-row list
 * editor that replaced it (a comma-joined string could not represent a
 * document label that itself contains a comma without ambiguity).
 */

const RESOLVED_SETTING_KEYS = [
  "rent_control_cap",
  "co_inspection_rule",
  "tax_portal_url",
  "tax_lookup_format",
  "water_sewer_biller",
  "county_tax_board",
  "appeal_deadline",
  "registration_office",
  "default_notice_period_days",
  "tax_grace_period_days",
  "received_vs_postmark_rule",
  "delinquent_interest_rate",
] as const satisfies readonly (keyof PropertyResource & string)[];

/** The eleven non-date entries in `RESOLVED_SETTING_KEYS` share this
 * provenance wording instead of `PROVENANCE_WARNING_SENTENCE` — see the
 * field-list comment where this is used. */
export const RESOLVED_SETTING_WARNING_SENTENCE =
  "This value is a PropDesk default and has not been confirmed for this property.";

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function formatYesNo(value: boolean | null): string {
  if (value === null) {
    return "—";
  }
  return value ? "Yes" : "No";
}

function formatMoney(value: string | null): string {
  if (value === null) {
    return "—";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return value;
  }
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(numeric);
}

/**
 * Story 15.8's own Property key-facts strip (Item 2, Judgement calls 1-3) —
 * built straight off `PropertyResource.key_facts`, the four figures `GET
 * /properties/{id}` now computes server-side (`app.domain.properties.
 * key_facts.compute_property_key_facts`). No client-side computation of
 * "is this occupied"/"is this in force" happens here at all (AD-2) — this
 * function only formats what the backend already resolved.
 *
 * **Judgement call 2's own label**: "Scheduled rent" — deliberately not the
 * two-word name this codebase already reserves elsewhere, for both Story
 * 6.7's own real saved view and the ledger-dependent, actual-collected
 * -income concept Epic 10/Story 13.2 deferred (`key_facts.in_force_
 * monthly_rent_cents`'s own docstring). This is a real, in-force-lease
 * -derived figure, not that other concept.
 *
 * **Judgement call 3's own overdue handling**: the compliance-deadline stat
 * carries an inline "Overdue" badge (`fill="overdue"`, mirroring `Backup
 * Status.tsx`'s identical, established precedent) whenever `key_facts.
 * soonest_compliance_deadline_overdue` is true — the date itself still
 * renders plainly (never hidden, never silently presented as upcoming).
 *
 * **Story 15.13** inserts a "Lease start" stat immediately before "Lease
 * end" (renamed here from "Next lease end" to match the AC's own literal
 * wording and mirror `TenantRecord.tsx`'s own adjacent "Lease start"/"Lease
 * end" pair exactly) — both built from `key_facts.soonest_lease_start_
 * date`/`key_facts.soonest_lease_end_date`, the same one resolved lease
 * (`app.domain.properties.key_facts`'s own module docstring: never two
 * independently-resolved dates). The empty-state wording stays "None
 * upcoming" — Story 15.8's own already-established text for this stat —
 * applied to both dates identically, rather than switching to `TenantRecord.
 * tsx`'s own "—": the AC only requires the two to agree with each other
 * (VC-15.13-02), not that this page's wording literally match a different
 * page's.
 */
/**
 * Owner-requested (2026-08-28): "update the card that says occupancy to be
 * Tenant's Name and if it is vacant to just say Vacant" — replaces the
 * previous "X of Y occupied" count with the real tenant name(s)
 * (`key_facts.tenant_names`, `app.repo.leases.property_lease_tenant_names`'s
 * own docstring: every co-tenant on every occupied unit, comma-joined,
 * ordered by unit then name). "No rentable units" is kept as its own,
 * more accurate state, distinct from "Vacant" — a property with nothing to
 * occupy at all is not the same honest reading as one with real rentable
 * units that happen to be empty right now.
 */
function occupancyValue(keyFacts: PropertyResource["key_facts"]): string {
  if (keyFacts.rentable_unit_count === 0) {
    return "No rentable units";
  }
  return keyFacts.tenant_names.length > 0 ? keyFacts.tenant_names.join(", ") : "Vacant";
}

function propertyKeyFacts(keyFacts: PropertyResource["key_facts"]): KeyFact[] {
  return [
    { label: "Scheduled rent", value: formatMoneyCell(keyFacts.in_force_monthly_rent_cents), tone: "money" },
    { label: "Tenant", value: occupancyValue(keyFacts) },
    {
      label: "Lease start",
      value: keyFacts.soonest_lease_start_date ? formatDateCell(keyFacts.soonest_lease_start_date) : "None upcoming",
    },
    {
      label: "Lease end",
      value: keyFacts.soonest_lease_end_date ? formatDateCell(keyFacts.soonest_lease_end_date) : "None upcoming",
    },
    {
      label: "Next compliance deadline",
      value: keyFacts.soonest_compliance_deadline_date
        ? formatDateCell(keyFacts.soonest_compliance_deadline_date)
        : "None on file",
      ...(keyFacts.soonest_compliance_deadline_overdue ? { badge: <Badge label="Overdue" fill="overdue" /> } : {}),
    },
  ];
}

/**
 * Story 15.8's own Property status pill (Judgement call 4): an archived
 * property keeps its existing "Archived" badge (VC-5.6-15, unchanged); a
 * live property's pill is occupancy-derived, reusing the exact figures
 * `propertyKeyFacts` above already renders — never a second, independent
 * occupancy computation. Mirrors EXPERIENCE.md's own status-to-badge table
 * exactly: "Vacant" and a partial count are both named `neutral` there
 * ("Vacant" appears in that table's own `neutral` row); "Active lease in
 * force" is `settled`, which a fully-occupied property is the property
 * -level equivalent of. `null` only when a property has no rentable units
 * at all (no single honest occupancy concept to show a pill for at all —
 * the alternative Judgement call 4 itself names).
 */
function propertyStatusBadge(keyFacts: PropertyResource["key_facts"]) {
  const { occupied_unit_count, rentable_unit_count } = keyFacts;
  if (rentable_unit_count === 0) {
    return null;
  }
  if (occupied_unit_count === 0) {
    return <Badge label="Vacant" fill="neutral" />;
  }
  if (occupied_unit_count === rentable_unit_count) {
    return <Badge label="Fully occupied" fill="settled" />;
  }
  return <Badge label={`${occupied_unit_count} of ${rentable_unit_count} occupied`} fill="neutral" />;
}

/** `disposition_closing_documents`'s own resting-state renderer (Tier A fix
 * round, 2026-08-20, Fix 1): a plain list of strings, unchanged from before
 * the fix. This field is no longer edited through `InlineEditCell` at all
 * (see `DispositionClosingDocumentsField` below), so this function only
 * ever sees the field's real `string[] | null` API type — no synthetic
 * string-draft case to tolerate any more. */
function formatClosingDocuments(value: string[] | null): string {
  return value && value.length > 0 ? value.join(", ") : "—";
}

/** `1234567` -> "1.18 MB" — mirrors `BackupStatus.tsx`'s/`DocumentsList.
 * tsx`'s own identical, deliberately local helper (no shared byte-formatter
 * exists yet). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatResolvedValue(resolved: ResolvedSettingResource): string {
  if (resolved.value === null || resolved.value === undefined) {
    return "—";
  }
  return String(resolved.value);
}

// Owner-caught, 2026-08-28: the twelve resolved municipal settings' own
// provenance-warning wiring (a `provenanceFor` mapping `ResolvedSettingResource`
// to `RecordFieldProvenance`, once passed as each field's `provenance`) is
// gone — see the field-list comment further down for the full reasoning.

/** Judgement call 5's own plain-language confirmation copy: what stops and
 * what is kept, never a claim about a capability this codebase doesn't have
 * yet (no lease/tenant/rent-history/tax-report claim — `Lease`/`Tenant`
 * don't exist until Epic 6, and no generator of any kind exists yet
 * either). */
function archiveDescription(nickname: string): string {
  return (
    `Archiving removes ${nickname} from your active portfolio list. Once PropDesk generates ` +
    "future rent charges, tax schedules and compliance cycles, it will stop doing so for this " +
    `property. Everything already on record for ${nickname} stays exactly as it is, and it's ` +
    "still readable behind the \"Show archived\" filter — it is never deleted. You can " +
    "un-archive it in one click, any time."
  );
}

/** Tier A fix round, 2026-08-20, Fix 2: no longer claims the whole record
 * "stays fully visible and editable" — an archived property's core fields
 * (`full_address`, `year_built`, `mortgage_servicer`, ...) are preserved
 * as-is and are no longer editable once archived; only the five disposition
 * fields remain so. Every field is still fully *visible* either way — the
 * "never hidden" half of this sentence is unchanged. */
const ARCHIVED_EXPLANATION =
  "This property is archived. Its whole record — including the disposition fields below — " +
  "stays fully visible, and it's excluded from the active portfolio list unless \"Show archived\" " +
  "is on. Its core fields are preserved as-is; only the disposition fields below remain editable. " +
  "Un-archive it first if you need to change anything else.";

/** The value half of `PATCH /properties/{id}`'s body a commit produces. An
 * emptied text field is sent as `null` (mirrors `MunicipalityRecord.tsx`'s
 * identical `patchBodyFor` — a cleared value returns the field to
 * "unanswered", not to an empty string a numeric/date column would refuse
 * outright).
 *
 * `disposition_closing_documents` no longer goes through this function at
 * all (Tier A fix round, 2026-08-20, Fix 1) — it has its own dedicated
 * multi-row editor (`DispositionClosingDocumentsField`) and its own save
 * path (`saveClosingDocuments`), because the field's real API type,
 * `string[] | null`, cannot be represented as one comma-joined string
 * without an unresolvable ambiguity the instant a real entry (a filename
 * like "Deed, Signed.pdf") itself contains a comma. */
function patchBodyFor(fieldKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for field "${fieldKey}"`);
  }
  return { [fieldKey]: payload.value === "" ? null : payload.value };
}

/**
 * `disposition_closing_documents`'s own real edit UI (Tier A review, Tier A
 * fix round, 2026-08-20, Fix 1): a proper multi-row list editor — one text
 * input per document entry, an "add another" affordance, and a per-row
 * "Remove" once more than one row exists — exactly mirroring
 * `AddPropertyWizard.tsx`'s own `unitLabels` pattern, rather than the single
 * comma-separated text field this used to be. That old shape silently
 * corrupted any entry that itself contained a comma (a completely plausible
 * real filename, e.g. "Deed, Signed.pdf") the moment it was re-edited and
 * re-saved: the pre-filled draft was indistinguishable from two entries,
 * and splitting on "," permanently lost the fact it was one label. Each row
 * is its own field here — there is no delimiter to be ambiguous about.
 *
 * Not routed through `RecordPage`'s own `InlineEditCell` mechanism at all
 * (no `edit` config on this field in `PropertyRecord`'s own field list):
 * that mechanism is fundamentally a single-text-input editor, with no shape
 * for "add/remove a row". This component owns its own editing/saving state
 * and calls `onSave` directly — the same `PATCH` request every other field
 * on this page ends up sending, just built here rather than through
 * `patchBodyFor`.
 */
function DispositionClosingDocumentsField({
  value,
  editable,
  onSave,
}: {
  value: string[] | null;
  editable: boolean;
  onSave: (documents: string[] | null) => Promise<EditCommitOutcome>;
}) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<string[]>(value && value.length > 0 ? value : [""]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  function startEditing() {
    if (!editable) {
      return;
    }
    setRows(value && value.length > 0 ? value : [""]);
    setError(undefined);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setError(undefined);
  }

  //: Blank rows are filtered out on save (mirrors `AddPropertyWizard.tsx`'s
  //: own `trimmedUnitLabels`) — an "Add another document" row left empty
  //: never becomes a stored `""` entry.
  async function handleSave() {
    const trimmed = rows.map((row) => row.trim()).filter((row) => row.length > 0);
    setIsSaving(true);
    setError(undefined);
    const outcome = await onSave(trimmed.length > 0 ? trimmed : null);
    setIsSaving(false);
    if (outcome.ok) {
      setEditing(false);
    } else {
      setError(outcome.message ?? "Something went wrong. Please try again in a moment.");
    }
  }

  const resting = formatClosingDocuments(value);

  if (!editing) {
    if (!editable) {
      return <>{resting}</>;
    }
    // The same resting-click-to-edit affordance shape `InlineEditCell`
    // itself renders (a full-width, unstyled button) — clicking anywhere in
    // the resting value enters edit mode, mirroring every other editable
    // field row on this page.
    return (
      <button
        type="button"
        onClick={startEditing}
        className="block w-full cursor-default border-0 bg-transparent p-0 text-left font-[inherit] text-[inherit]"
      >
        {resting}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-3" onClick={(event) => event.stopPropagation()}>
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            aria-label={`Closing document ${index + 1}`}
            value={row}
            onChange={(event) =>
              setRows((existing) => existing.map((entry, i) => (i === index ? event.target.value : entry)))
            }
          />
          {rows.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRows((existing) => existing.filter((_, i) => i !== index))}
            >
              Remove
            </Button>
          ) : null}
        </div>
      ))}
      <Button type="button" variant="ghost" className="self-start" onClick={() => setRows((existing) => [...existing, ""])}>
        + Add another document
      </Button>
      {error ? (
        <p role="alert" className="text-meta text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="button" onClick={() => void handleSave()} disabled={isSaving}>
          {isSaving ? "Saving…" : "Save"}
        </Button>
        <Button type="button" variant="ghost" onClick={cancelEditing} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function PropertyRecord() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  //: `PATCH /properties/{id}` is `role=Role.OWNER` (Boundaries & Constraints)
  //: — every edit affordance below must be withheld from anyone but a
  //: confirmed Owner, not merely "not Read-only" (mirrors `MunicipalityRecord.
  //: tsx`'s own Tier A fix: a Member is not Read-only, so `useIsReadOnly()`
  //: alone would render these fully editable for a Member, who would then
  //: 403 the moment they submitted).
  const isOwner = useIsOwner();
  //: `POST /documents` is `role=Role.MEMBER` (`app/api/documents.py`'s own
  //: module docstring: "a document is everyday portfolio content... the
  //: same category `app/api/activities.py`'s own communication log already
  //: put at `Role.MEMBER`") — the "Add document" affordance below is
  //: withheld only from a Read-only account, not from a Member, mirroring
  //: `SavedViewChips.tsx`'s own identical "Save this view" gate for the
  //: same role level.
  const isReadOnly = useIsReadOnly();

  const queryKey = useMemo(() => ["properties", "record", id] as const, [id]);

  const recordQuery = useQuery({
    queryKey,
    queryFn: () => apiFetch<PropertyResource>(`/properties/${id}`),
    enabled: id !== undefined,
  });

  //: Story 7.2 — this property's own attached documents, inline (AC's own
  //: fourth bullet). `page_size=200`: this section renders a compact table,
  //: never `DataTable`'s own pagination chrome (I/O matrix: "never the full
  //: shared-data-table chrome... inside a record page section"), so there
  //: is no page-2 control to reach a document past the first page with —
  //: 200 is `MAX_PAGE_SIZE`, the most this route can ever return in one call.
  const documentsQueryKey = useMemo(() => ["documents", "list", "for-property", id] as const, [id]);
  const documentsQuery = useQuery({
    queryKey: documentsQueryKey,
    queryFn: () =>
      apiFetch<Page<DocumentListItemResource>>(
        `/documents?property_id=${encodeURIComponent(id ?? "")}&page_size=200&sort=-uploaded_at`,
      ),
    enabled: id !== undefined,
  });
  const [addDocumentOpen, setAddDocumentOpen] = useState(false);
  //: Story 15.2's own "Replace" action — mirrors `DocumentsList.tsx`'s own
  //: identical `replaceTarget` shape: the target document a per-row action
  //: is currently acting on, driving a single shared dialog instance.
  const [replaceTarget, setReplaceTarget] = useState<DocumentListItemResource | null>(null);

  //: The same real category labels `AddDocumentDialog`'s own `<select>`
  //: renders — the compact table below shows a document's category by its
  //: real, Owner-facing label ("Insurance"), never the raw stored
  //: `ReferenceListItem.value` ("insurance") a family member never typed
  //: in or sees anywhere else (mirrors `DocumentsList.tsx`'s own identical
  //: `categoryLabelByValue` resolution).
  const documentCategoriesQuery = useQuery({
    queryKey: ["reference-list-items", "document_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=document_category"),
  });
  const documentCategoryLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of documentCategoriesQuery.data?.data ?? []) {
      map[item.value] = item.label;
    }
    return map;
  }, [documentCategoriesQuery.data]);

  const propertyTypesQuery = useQuery({
    queryKey: ["reference-list-items", "property_type"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=property_type"),
  });

  //: Story 12.1 — this property's own equipment, inline (AC's own
  //: `CompletenessMeter` placement, VC-12.1-09). `page_size=200`: same
  //: "fetch everything a screen this size will ever hold in one round
  //: trip" precedent as `documentsQuery` immediately above — this section
  //: renders a compact table, never `DataTable`'s own pagination chrome.
  const equipmentQueryKey = useMemo(() => ["equipment", "list", "for-property", id] as const, [id]);
  const equipmentQuery = useQuery({
    queryKey: equipmentQueryKey,
    queryFn: () => apiFetch<Page<EquipmentResource>>(`/equipment?property_id=${encodeURIComponent(id ?? "")}&page_size=200`),
    enabled: id !== undefined,
  });
  const [addEquipmentOpen, setAddEquipmentOpen] = useState(false);

  const equipmentCategoriesQuery = useQuery({
    queryKey: ["reference-list-items", "equipment_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=equipment_category"),
  });
  const equipmentCategoryLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of equipmentCategoriesQuery.data?.data ?? []) {
      map[item.value] = item.label;
    }
    return map;
  }, [equipmentCategoriesQuery.data]);

  useEffect(() => {
    document.title = recordQuery.data ? `${recordQuery.data.nickname} — PropDesk` : "Property — PropDesk";
  }, [recordQuery.data]);

  function writeRecordToCache(updated: PropertyResource) {
    queryClient.setQueryData<PropertyResource>(queryKey, updated);
  }

  async function sendPatch(
    row: PropertyResource,
    field: DataTableColumn<PropertyResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<PropertyResource>(`/properties/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBodyFor(field.key, payload)),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<PropertyResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  //: `disposition_closing_documents`'s own save path (Tier A fix round,
  //: 2026-08-20, Fix 1) — bypasses `patchBodyFor`/`commitEdit` entirely
  //: (this field is no longer a `DataTableColumn` edit) and sends the real
  //: `string[] | null` the multi-row editor built directly.
  async function saveClosingDocuments(documents: string[] | null): Promise<EditCommitOutcome> {
    if (recordQuery.data === undefined) {
      return { ok: false, message: "Something went wrong. Please try again in a moment." };
    }
    try {
      const updated = await apiFetch<PropertyResource>(`/properties/${recordQuery.data.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disposition_closing_documents: documents }),
      });
      writeRecordToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  //: The archive confirmation dialog's own open/in-flight/error state
  //: (VC-5.6-14: the dialog opens on click and fires zero `DELETE` calls;
  //: only confirming inside it fires the real one) — the identical shape
  //: `UserRecord.tsx`'s own `removeOpen`/`isRemoving`/`removeError` uses for
  //: its "Remove" action.
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | undefined>(undefined);
  //: A stable fallback focus target for the confirm dialog, mirroring
  //: `UserRecord.tsx`'s own `contentRef` — `RecordPage.tsx` owns the actual
  //: `<h1>` internally, so this page's own outer wrapper stands in instead.
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * Archive (spec judgement call 5): fires only once the confirmation
   * dialog's own confirm button is pressed — `DELETE /properties/{id}`
   * itself is completely unchanged (VC-5.6-01), this page just gates the
   * call behind a real dialog instead of firing it on the header button's
   * own click.
   */
  async function confirmArchive() {
    if (id === undefined || recordQuery.data === undefined) {
      return;
    }
    setIsArchiving(true);
    setArchiveError(undefined);
    try {
      // `DELETE /properties/{id}` returns only `{deleted: boolean}` — a
      // client-side merge of the one field it actually changed
      // (`is_archived`, derived server-side from `deleted_at`) is enough to
      // render this page's own new state correctly and needs no second
      // round trip; every other field on the record is untouched by
      // archiving (VC-5.6-01).
      await apiFetch<{ deleted: boolean }>(`/properties/${id}`, { method: "DELETE" });
      writeRecordToCache({ ...recordQuery.data, is_archived: true });
      setArchiveOpen(false);
    } catch (error) {
      setArchiveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsArchiving(false);
    }
  }

  /**
   * Un-archive (spec judgement call 5): a direct primary action, no
   * confirmation dialog — recovery, not a destructive action (VC-5.6-16),
   * mirroring `UserRecord.tsx::handleRestore`'s identical reasoning and its
   * own live-toast choice: the page's own fields already show the change
   * (the badge and sentence disappear), but the property becoming active
   * again is an event on the server, not a visible transformation of this
   * page's own layout the toast is standing in for.
   */
  async function handleRestore() {
    if (id === undefined) {
      return;
    }
    setIsRestoring(true);
    setRestoreError(undefined);
    try {
      const restored = await apiFetch<PropertyResource>(`/properties/${id}/restore`, { method: "POST" });
      writeRecordToCache(restored);
      showToast(`${restored.nickname} is active again.`);
    } catch (error) {
      setRestoreError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRestoring(false);
    }
  }

  //: Tier A fix round, 2026-08-20, Fix 2: computed here (moved above the
  //: `fields` memo below, which now depends on it) rather than only near the
  //: bottom of this component — an archived property locks every non-
  //: disposition field to read-only, so the field list itself needs to know
  //: the archive state, not just the header's badge/actions.
  const isArchived = recordQuery.data?.is_archived ?? false;

  const fields = useMemo<RecordField<PropertyResource>[]>(() => {
    const propertyTypeOptions = (propertyTypesQuery.data?.data ?? []).map((item) => ({
      value: item.value,
      label: item.label,
    }));
    const propertyTypeLabelByValue = Object.fromEntries(
      propertyTypeOptions.map((option) => [option.value, option.label]),
    ) as Record<string, string>;

    // `PATCH /properties/{id}` is `role=Role.OWNER` (Boundaries & Constraints:
    // "Role.OWNER gates every write"), mirroring `MunicipalityRecord.tsx`'s own
    // identical Tier A fix — `RecordPage`'s own default gate is merely
    // `!isReadOnly`, which a Member also satisfies, and a Member is not an
    // Owner, so every edit affordance here is withheld unless `isOwner` is
    // actually confirmed true.
    const textEditConfig = { kind: "text" } as const;
    const yesNoEditConfig = { kind: "select", options: YES_NO_OPTIONS } as const;
    const propertyTypeEditConfig =
      propertyTypeOptions.length > 0 ? ({ kind: "select", options: propertyTypeOptions } as const) : undefined;
    //: Present only for a confirmed Owner AND, per Tier A fix round Fix 2,
    //: only for a *live* property — `PATCH /properties/{id}` now refuses
    //: (409) any non-disposition field on an archived property
    //: (`app/api/properties.py::update_property`), so these core-field edit
    //: configs are withheld once archived, exactly the same withholding
    //: mechanism `fields.ts` already uses for "absent means read-only".
    //: `dispositionTextEdit` below is the one exception: the five
    //: disposition fields stay editable archived or not (judgement call 2).
    const textEdit = isOwner && !isArchived ? { edit: textEditConfig } : {};
    const yesNoEdit = isOwner && !isArchived ? { edit: yesNoEditConfig } : {};
    const propertyTypeEdit = isOwner && !isArchived && propertyTypeEditConfig ? { edit: propertyTypeEditConfig } : {};
    //: The disposition record's own four scalar fields (everything but
    //: `disposition_closing_documents`, which has no `edit` config at all —
    //: see `DispositionClosingDocumentsField`) — editable for an Owner
    //: whether the property is archived or not (spec judgement call 2, I/O
    //: matrix: "PATCH ... on an archived property, setting disposition_date/
    //: disposition_sale_price/etc.: succeeds").
    const dispositionTextEdit = isOwner ? { edit: textEditConfig } : {};

    const definitions: RecordField<PropertyResource>[] = [
      // -- The five fields that matter (I/O matrix) — render immediately. -- //
      { key: "nickname", ...textEdit },
      { key: "full_address", ...textEdit },
      { key: "municipality_name" },
      {
        // Mirrors PropertiesList.tsx's own `property_type` fix (commit
        // cad93d8): resolve the raw stored enum through the same
        // reference-list label map used to build the edit dropdown's own
        // options, rather than falling through to the raw value.
        key: "property_type",
        render: (row) => propertyTypeLabelByValue[row.property_type] ?? row.property_type,
        ...propertyTypeEdit,
      },
      { key: "year_built", ...textEdit },
      // -- Everything else — behind "More details". ------------------------ //
      { key: "county", primary: false, ...textEdit },
      { key: "block_and_lot", primary: false, ...textEdit },
      {
        key: "built_before_1978",
        primary: false,
        render: (row) => formatYesNo(row.built_before_1978),
        ...yesNoEdit,
      },
      { key: "square_footage", primary: false, ...textEdit },
      { key: "unit_count", primary: false, ...textEdit },
      {
        key: "purchase_date",
        primary: false,
        render: (row) => (row.purchase_date ? formatDateCell(row.purchase_date) : "—"),
        ...textEdit,
      },
      {
        key: "purchase_price",
        primary: false,
        render: (row) => formatMoney(row.purchase_price),
        ...textEdit,
      },
      {
        key: "current_estimated_value",
        primary: false,
        render: (row) => formatMoney(row.current_estimated_value),
        ...textEdit,
      },
      { key: "mortgage_servicer", primary: false, ...textEdit },
      { key: "loan_number", primary: false, ...textEdit },
      {
        key: "taxes_escrowed_by_lender",
        primary: false,
        render: (row) => formatYesNo(row.taxes_escrowed_by_lender),
        ...yesNoEdit,
      },
      { key: "insurance_carrier", primary: false, ...textEdit },
      { key: "insurance_policy_number", primary: false, ...textEdit },
      {
        key: "insurance_renewal_date",
        primary: false,
        render: (row) => (row.insurance_renewal_date ? formatDateCell(row.insurance_renewal_date) : "—"),
        ...textEdit,
      },
      { key: "registration_number", primary: false, ...textEdit },
      {
        key: "registration_filing_date",
        primary: false,
        render: (row) => (row.registration_filing_date ? formatDateCell(row.registration_filing_date) : "—"),
        ...textEdit,
      },
      {
        key: "rent_control_flag",
        primary: false,
        render: (row) => formatYesNo(row.rent_control_flag),
        ...yesNoEdit,
      },
      { key: "notes", primary: false, ...textEdit },
      {
        key: "photos",
        primary: false,
        render: (row) => (row.photos && row.photos.length > 0 ? `${row.photos.length} photo(s)` : "—"),
      },
      // -- The disposition record (Story 5.6) — editable independently of --
      // -- archiving itself (judgement call 2), never demanded upfront -----
      // -- (judgement call 6). ----------------------------------------------
      {
        key: "disposition_date",
        primary: false,
        render: (row) => (row.disposition_date ? formatDateCell(row.disposition_date) : "—"),
        ...dispositionTextEdit,
      },
      {
        key: "disposition_sale_price",
        primary: false,
        render: (row) => formatMoney(row.disposition_sale_price),
        ...dispositionTextEdit,
      },
      { key: "disposition_buyer", primary: false, ...dispositionTextEdit },
      {
        key: "disposition_capital_improvements",
        primary: false,
        render: (row) => formatMoney(row.disposition_capital_improvements),
        ...dispositionTextEdit,
      },
      // No `edit` config here at all (Tier A fix round, 2026-08-20, Fix 1):
      // this field's own real edit UI is the multi-row `Disposition
      // ClosingDocumentsField` below, entirely outside `RecordPage`'s
      // `InlineEditCell` mechanism — `render` itself decides resting vs.
      // editing. `editable` stays true archived or not (judgement call 2,
      // same as every other disposition field above).
      {
        key: "disposition_closing_documents",
        primary: false,
        render: (row) => (
          <DispositionClosingDocumentsField
            value={row.disposition_closing_documents}
            editable={isOwner}
            onSave={saveClosingDocuments}
          />
        ),
      },
      // -- The twelve inherited municipal settings (AD-9, judgement call 8). //
      // Owner-caught, 2026-08-28: the provenance warning ("This value is a
      // PropDesk default and has not been confirmed for this property.")
      // is gone from these twelve fields entirely, not only when a field
      // has no resolved value — live feedback on "Default notice period
      // (days)" (a field with a real resolved value, "30") confirmed the
      // sentence itself was unwanted here regardless of whether a value is
      // present, not only confusing next to a blank one. `provenance` is no
      // longer wired at all, so `needsProvenanceWarning` never sees a real
      // `RecordFieldProvenance` for any of these twelve and always renders
      // plainly. `provenanceFor`/`RESOLVED_SETTING_WARNING_SENTENCE` stay
      // defined (unused) rather than deleted — the same real AD-9 tier/
      // confirmed-on/usable data is still genuinely available from
      // `ResolvedSettingResource` the moment a future story wants this
      // signal back in some other shape.
      ...RESOLVED_SETTING_KEYS.map(
        (key): RecordField<PropertyResource> => ({
          key,
          primary: false,
          render: (row) => formatResolvedValue(row[key]),
        }),
      ),
    ];
    return definitions;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `saveClosingDocuments` is a stable
    // per-render closure over `recordQuery.data`/`writeRecordToCache`, not a dependency this list
    // needs to react to independently of `isOwner`/`isArchived`/`propertyTypesQuery.data`.
  }, [propertyTypesQuery.data, isOwner, isArchived]);

  const status: "loading" | "not-found" | "error" | "ready" = recordQuery.isPending
    ? "loading"
    : recordQuery.isError
      ? recordQuery.error instanceof ApiError && recordQuery.error.status === 404
        ? "not-found"
        : "error"
      : "ready";

  const errorMessage = recordQuery.error instanceof ApiError ? recordQuery.error.message : undefined;

  //: The inline documents section (AC's own fourth bullet, VC-7.2-09/-10):
  //: a compact table when the property has documents, a real empty-state
  //: ghost "Add" button when it doesn't — `RelatedRecordsSection`'s own
  //: existing empty/non-empty branching handles both, this just supplies
  //: the rows and the one real per-row action (`RelatedRecords.tsx`'s own
  //: new, generic `rowActions` slot, Story 7.2). Rendered as an empty
  //: `rows: []` section while `documentsQuery` is still pending — the
  //: identical "loading reads as empty, briefly" shape every other list on
  //: this page already lives with before its own first fetch resolves.
  const documentRows: RelatedRecordRow[] = (documentsQuery.data?.data ?? []).map((document) => ({
    id: document.id,
    original_filename: document.original_filename,
    category: document.category,
    size_bytes: document.size_bytes,
    uploaded_at: document.uploaded_at,
    //: Story 15.2 (VC-15.2-05) — resolved fresh on every read, never a
    //: cached/stale label; see the `replaced_by_document_id` column below.
    replaced_by_document_id: document.replaced_by_document_id,
    replaced_by_label: document.replaced_by_label,
    replaced_by_url: document.replaced_by_url,
  }));

  //: Story 12.1's own equipment section — mirrors the documents section
  //: immediately above exactly (compact table, real empty-state, ghost
  //: "Add" button). `equipmentRows` stays `[]` while `equipmentQuery` is
  //: still pending, the identical "loading reads as empty, briefly" shape.
  const equipmentRows: RelatedRecordRow[] = (equipmentQuery.data?.data ?? []).map((item) => ({
    id: item.id,
    category: equipmentCategoryLabelByValue[item.category] ?? item.category,
    make_model: item.make && item.model_number ? `${item.make} ${item.model_number}` : (item.make ?? item.model_number ?? "—"),
    status: EQUIPMENT_STATUS_LABELS[item.status] ?? item.status,
  }));

  //: VC-12.1-09: the real count of this property's own equipment still in
  //: `details_needed` against its total — never a stubbed or hardcoded
  //: count, computed straight from `equipmentQuery.data`'s own real rows.
  //: `undefined` (never rendered) for a property with zero equipment rows
  //: — the "no empty section" discipline Story 11.6's own rent-review
  //: section already established, mirrored here exactly.
  const equipmentRowsAll = equipmentQuery.data?.data ?? [];
  //: Story 12.5's own required fix (this story's own Intent): `replaced`/
  //: `removed` rows are real history, never current inventory — excluded
  //: from BOTH the numerator and the denominator, not only the numerator.
  //: An old, retired boiler sitting in the "done" column was never what
  //: "done" means here; a property whose only equipment is entirely
  //: retired now correctly renders no meter at all (the same "zero rows,
  //: zero meter" rule below, now measuring zero CURRENT rows rather than
  //: zero rows of any kind).
  const equipmentCurrentRows = equipmentRowsAll.filter(
    (item) => item.status !== "replaced" && item.status !== "removed",
  );
  const equipmentActiveCount = equipmentCurrentRows.filter((item) => item.status !== "details_needed").length;

  //: The one "Add document" action, shared between the documents section's
  //: `emptyState.action` (VC-7.2-09's own zero-rows ghost button) and its
  //: new populated-section `action` (Fix 1, Tier B fix round, 2026-08-20) —
  //: one action, wired to both branches `RelatedRecordsSection` renders,
  //: rather than two separately-maintained copies that could drift.
  //: `undefined` for a Read-only account (mirrors every other write
  //: affordance on this page): neither branch renders a button at all.
  const addDocumentAction = isReadOnly
    ? undefined
    : {
        label: "Add document",
        onClick: () => setAddDocumentOpen(true),
        // VC-7.2-09: the AC's own explicit "empty-state ghost Add button"
        // — a record page already has its own primary header action, and
        // the populated-section header button (Fix 1) mirrors the same
        // lower-emphasis styling for consistency.
        variant: "ghost" as const,
      };

  //: Story 12.1's own "Add equipment" action — mirrors `addDocumentAction`
  //: immediately above exactly (`undefined` for Read-only, shared between
  //: the equipment section's empty state and its populated-section header
  //: button).
  const addEquipmentAction = isReadOnly
    ? undefined
    : {
        label: "Add equipment",
        onClick: () => setAddEquipmentOpen(true),
        variant: "ghost" as const,
      };

  return (
    <div ref={contentRef} tabIndex={-1} className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      {restoreError ? (
        <p role="alert" className="mb-4 text-meta text-danger">
          {restoreError}
        </p>
      ) : null}
      {/* VC-5.6-15: the "Archived" badge and its explanatory sentence —
          never hidden, the record itself (every field below, including the
          disposition fields) stays fully visible regardless. No `deletion`
          prop is passed to `RecordPage` (see this file's own top-of-file
          doc comment): unlike a removed user, the disposition fields stay
          editable — Tier A fix round, 2026-08-20, Fix 2: every other field
          does not, once archived (see `isArchived`'s own declaration above,
          near the `fields` memo). */}
      {isArchived ? (
        <p className="mb-4 text-meta text-ink-secondary">{ARCHIVED_EXPLANATION}</p>
      ) : null}
      {/* Story 12.1, VC-12.1-09: the real count of this property's own
          equipment still in `details_needed` against its total — only
          rendered once this property actually has at least one CURRENT
          equipment row (`equipmentCurrentRows.length > 0` — Story 12.5's
          own fix: a property whose only equipment is entirely `replaced`/
          `removed` renders no meter at all, mirrored here exactly), never
          a stubbed or empty section (mirrors Story 11.6's own rent-review
          "no empty section" discipline). Placed near the top, alongside
          the archived-state explanation, rather than at the very bottom
          past the Documents/Equipment tables below — a summary figure, not
          one more row in a list. */}
      {equipmentCurrentRows.length > 0 ? (
        <div className="mb-6">
          <p className="mb-2 text-label font-medium text-ink-secondary">Equipment completeness</p>
          <CompletenessMeter completed={equipmentActiveCount} total={equipmentCurrentRows.length} />
        </div>
      ) : null}
      <RecordPage<PropertyResource>
        fields={fields}
        row={recordQuery.data}
        status={status}
        {...(errorMessage ? { errorMessage } : {})}
        identifier={(row) => row.nickname}
        backLink={{ label: "Properties", href: "/properties" }}
        icon={PropertiesIcon}
        tint="indigo"
        statusBadge={(row) =>
          row.is_archived ? <Badge label="Archived" fill="neutral" /> : propertyStatusBadge(row.key_facts)
        }
        keyFacts={(row) => <KeyFactsStrip facts={propertyKeyFacts(row.key_facts)} />}
        {...(isArchived
          ? {
              primaryAction: {
                label: isRestoring ? "Un-archiving…" : "Un-archive",
                onClick: () => void handleRestore(),
                disabled: isRestoring,
              },
            }
          : {})}
        {...(!isArchived && isOwner && recordQuery.data
          ? {
              secondaryActions: [
                {
                  label: "Archive",
                  ariaLabel: `Archive ${recordQuery.data.nickname}`,
                  onClick: () => setArchiveOpen(true),
                },
              ],
            }
          : {})}
        onCommitEdit={commitEdit}
        {...(recordQuery.data
          ? {
              relatedRecords: [
                {
                  title: "Documents",
                  itemNamePlural: "documents",
                  rows: documentRows,
                  columns: [
                    { key: "original_filename", label: labelFor("original_filename") },
                    {
                      key: "category",
                      label: labelFor("category"),
                      render: (row) =>
                        row.category
                          ? (documentCategoryLabelByValue[row.category as string] ?? String(row.category))
                          : "—",
                    },
                    {
                      // DESIGN.md § Typography: tabular figures are
                      // mandatory on "every money column, every date
                      // column, every count" — live-caught, this table
                      // never applied it to either.
                      key: "size_bytes",
                      label: labelFor("size_bytes"),
                      tabularFigures: true,
                      render: (row) => formatBytes(row.size_bytes as number),
                    },
                    {
                      key: "uploaded_at",
                      label: labelFor("uploaded_at"),
                      tabularFigures: true,
                      render: (row) => formatDateCell(row.uploaded_at as string),
                    },
                    {
                      // Story 15.2 (VC-15.2-05) — resolved fresh via
                      // `documentRows`' own mapping above, never a
                      // cached/stale label. Mirrors `DocumentsList.tsx`'s
                      // own identical column exactly, including its own
                      // plain-`<a>`-not-`Link` reasoning: `replaced_by_url`
                      // always resolves to `/documents/{id}`, the real
                      // authenticated DOWNLOAD route — no document record
                      // page exists anywhere in this codebase.
                      key: "replaced_by_document_id",
                      label: labelFor("replaced_by_document_id"),
                      render: (row) =>
                        row.replaced_by_label && row.replaced_by_url ? (
                          <>
                            Superseded — replaced by{" "}
                            <a
                              href={row.replaced_by_url as string}
                              className="text-ink underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {row.replaced_by_label as string}
                            </a>
                          </>
                        ) : (
                          "—"
                        ),
                    },
                  ],
                  // No document record page exists (`app/domain/documents/
                  // __init__.py`'s own doc comment) — self-links back to
                  // this same property page, mirroring `DocumentsList.tsx`'s
                  // own identical "no record page" `getRowHref` convention;
                  // the real action is the `rowActions` Download link below.
                  getHref: () => `/properties/${id}`,
                  // Story 7.4: a real, authenticated bulk-download link —
                  // renders for every role, including Read-only (VC-7.4-06:
                  // "reading is what Read-only is for"), unlike
                  // `addDocumentAction` immediately below, which is `undefined`
                  // for that role. A plain `<a href>` to the streaming route,
                  // the same shape the per-row "Download" link already uses,
                  // not a JS click handler.
                  headerLink: { label: "Download all documents", href: `/properties/${id}/documents/download` },
                  rowActions: (row) => (
                    <div className="flex items-center gap-1">
                      <a
                        href={`/documents/${row.id}`}
                        className="inline-flex h-tap-target-min items-center rounded-md px-3 text-button text-accent hover:bg-surface-hover"
                      >
                        Download
                      </a>
                      {/* Story 15.2 — a real "Replace" action next to
                          Download, mirroring `DocumentsList.tsx`'s own
                          identical placement and gating (withheld for
                          Read-only, and for a row already replaced once —
                          VC-15.2-04). */}
                      {!isReadOnly && row.replaced_by_document_id == null ? (
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={`Replace ${row.original_filename}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            const document = (documentsQuery.data?.data ?? []).find((d) => d.id === row.id);
                            if (document) {
                              setReplaceTarget(document);
                            }
                          }}
                        >
                          Replace
                        </Button>
                      ) : null}
                    </div>
                  ),
                  // Fix 1 (Tier B fix round, 2026-08-20): the populated-
                  // section header "Add document" affordance
                  // (`RelatedRecordsSection`'s own new `action` prop) — the
                  // same `addDocumentAction` that `emptyState.action` below
                  // wires up for the zero-rows case. Before this fix, once
                  // a property had 1+ documents there was no way anywhere
                  // in the UI to reach `setAddDocumentOpen(true)` — the
                  // empty-state branch that owned the only "Add" button had
                  // already stopped rendering.
                  ...(addDocumentAction ? { action: addDocumentAction } : {}),
                  emptyState: {
                    body: "Add a lease, deed, insurance certificate, or any other file for this property.",
                    ...(addDocumentAction ? { action: addDocumentAction } : {}),
                  },
                },
                // Story 12.1's own equipment section — mirrors the
                // documents section immediately above exactly.
                {
                  title: "Equipment",
                  itemNamePlural: "equipment records",
                  rows: equipmentRows,
                  columns: [
                    { key: "category", label: labelFor("category") },
                    { key: "make_model", label: "Make / model" },
                    { key: "status", label: labelFor("status") },
                  ],
                  getHref: (row) => `/equipment/${row.id}`,
                  ...(addEquipmentAction ? { action: addEquipmentAction } : {}),
                  emptyState: {
                    body: "Log the first piece of equipment for this property — photo the data plate, pick a category, type the serial, save.",
                    ...(addEquipmentAction ? { action: addEquipmentAction } : {}),
                  },
                },
              ],
            }
          : {})}
      />
      {recordQuery.data && !isReadOnly ? (
        <AddDocumentDialog
          open={addDocumentOpen}
          onOpenChange={setAddDocumentOpen}
          propertyId={recordQuery.data.id}
          onUploaded={() => {
            void queryClient.invalidateQueries({ queryKey: documentsQueryKey });
            showToast("Document added.");
          }}
        />
      ) : null}
      {replaceTarget ? (
        <ReplaceDocumentDialog
          open={replaceTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setReplaceTarget(null);
            }
          }}
          oldDocument={replaceTarget}
          onReplaced={(result: DocumentReplacementResource) => {
            void queryClient.invalidateQueries({ queryKey: documentsQueryKey });
            showToast(`Replaced with ${result.new_document.original_filename}.`);
          }}
        />
      ) : null}
      {recordQuery.data && !isReadOnly ? (
        <AddEquipmentDialog
          open={addEquipmentOpen}
          onOpenChange={setAddEquipmentOpen}
          defaultPropertyId={recordQuery.data.id}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: equipmentQueryKey });
            showToast("Equipment added.");
          }}
        />
      ) : null}
      {recordQuery.data ? (
        <ConfirmDialog
          open={archiveOpen}
          onOpenChange={(open) => {
            setArchiveOpen(open);
            if (!open) {
              setArchiveError(undefined);
            }
          }}
          title="Archive this property?"
          description={archiveDescription(recordQuery.data.nickname)}
          confirmLabel={`Archive ${recordQuery.data.nickname}`}
          confirmingLabel="Archiving…"
          isConfirming={isArchiving}
          onConfirm={() => void confirmArchive()}
          fallbackFocusRef={contentRef}
          {...(archiveError ? { errorMessage: archiveError } : {})}
        />
      ) : null}
    </div>
  );
}
